"use client";

import Image from "next/image";
import { useCart } from "./CartContext";
import { Card, Link } from "@cappelendamm/design-system/react";
import FallbackImage from "@/components/Rosett.svg";
import NextLink from "next/link";
import { getPageLink } from "@/navigation/selectors";
import {
  ECommerceItemLocation,
  LOCATIONS_IDS,
} from "@/tracking/e-commerce-events";
import { PiMinus, PiPlus, PiTrash } from "react-icons/pi";
import classNames from "classnames";
import { getCartNumberOfItems, isFreightItem } from "./selectors";
import { Price } from "@/products/components/Price";
import { getPriceOffer } from "@/products/pricing";

const location: ECommerceItemLocation = { location_id: LOCATIONS_IDS.CART };

export function CartItems({ className }: { className?: string }) {
  const { cart, increaseQuantity, decreaseQuantity, removeItem, isPending } =
    useCart();
  if (getCartNumberOfItems(cart) === 0)
    return (
      <Card className={classNames("p-4", className)}>
        Du har ingen produkter i handlekurven
      </Card>
    );
  return (
    <Card
      className={classNames("max-h-full overflow-y-auto p-4", className)}
      as={"ul"}
    >
      {cart?.items
        .filter((item) => !isFreightItem(item))
        .map(
          ({
            name,
            images,
            price,
            variant: {
              sku,
              product: { path },
            },
            originalPrice,
            priceLists,
            quantity,
          }) => {
            return (
              <li
                key={sku}
                className={classNames(
                  "border-b py-4 first-of-type:pt-0 last-of-type:border-none last-of-type:pb-0",
                  { "animate-pulse": path === "loading" },
                )}
              >
                <article className="grid grid-cols-[auto_1fr] items-center gap-4">
                  <div className="flex justify-center rounded-lg bg-gray-100 p-3">
                    {images?.[0]?.url ? (
                      <Image
                        src={images?.[0]?.url || ""}
                        height={200}
                        width={84}
                        alt={`Omslag av ${name}`}
                      />
                    ) : (
                      <Image
                        src={FallbackImage}
                        alt={`Manglende bilde - Viser Cappelendamm logo`}
                        height={200}
                        width={84}
                      />
                    )}
                  </div>
                  <Link
                    as={NextLink}
                    variant="menu"
                    href={getPageLink({ pageType: "book", params: sku })}
                  >
                    {name}
                  </Link>
                  <div
                    className={classNames(
                      "grid w-fit grid-cols-3 items-center justify-center gap-2 rounded-full border px-2 py-1",
                    )}
                  >
                    {quantity > 1 ? (
                      <button
                        className="rounded-xl px-1 hover:bg-gray-50 focus:cvr-focus disabled:opacity-25"
                        type="button"
                        disabled={isPending}
                        onClick={async () => {
                          await decreaseQuantity({ sku, location });
                        }}
                      >
                        <span className="sr-only">{`Minsk antall til ${quantity - 1}`}</span>
                        <PiMinus size={24} />
                      </button>
                    ) : (
                      <button
                        className="rounded-xl px-1 hover:bg-gray-50 focus:cvr-focus disabled:opacity-25"
                        type="button"
                        disabled={isPending}
                        onClick={async () => {
                          await removeItem({ sku, location });
                        }}
                      >
                        <span className="sr-only">{`Fjern ${name}`}</span>
                        <PiTrash size={24} />
                      </button>
                    )}
                    <div
                      className={classNames("flex items-center justify-center")}
                    >
                      {quantity}
                    </div>
                    <button
                      className="rounded-xl px-0.5 hover:bg-gray-50 focus:cvr-focus disabled:opacity-25"
                      type="button"
                      disabled={isPending}
                      onClick={async () => {
                        await increaseQuantity({ sku, location });
                      }}
                    >
                      <span className="sr-only">{`Øk antall til ${quantity + 1}`}</span>
                      <PiPlus size={24} />
                    </button>
                  </div>
                  <div className="flex justify-self-end">
                    <Price
                      variant="previous-left"
                      regularFontSize
                      className={classNames(
                        "justify-self-end",
                        "text-lg",
                        "font-normal",
                      )}
                      {...getPriceOffer({
                        originalItemPrice: originalPrice,
                        total: price.gross,
                        quantity,
                        priceLists,
                      })}
                    />
                  </div>
                </article>
              </li>
            );
          },
        )}
    </Card>
  );
}
